import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin, Clock, Facebook, Twitter, Instagram, Linkedin, Youtube, Zap, Award } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const { language, t } = useLanguage()

  const getSocialLabel = (platform: 'twitter' | 'facebook' | 'instagram' | 'youtube' | 'linkedin') => {
    const labels = {
      tr: {
        twitter: 'Thinklyze Twitter hesabı',
        facebook: 'Thinklyze Facebook hesabı',
        instagram: 'Thinklyze Instagram hesabı',
        youtube: 'Thinklyze YouTube kanalı',
        linkedin: 'Thinklyze LinkedIn sayfası',
      },
      en: {
        twitter: 'Thinklyze on Twitter',
        facebook: 'Thinklyze on Facebook',
        instagram: 'Thinklyze on Instagram',
        youtube: 'Thinklyze on YouTube',
        linkedin: 'Thinklyze on LinkedIn',
      },
      ru: {
        twitter: 'Thinklyze в Twitter',
        facebook: 'Thinklyze в Facebook',
        instagram: 'Thinklyze в Instagram',
        youtube: 'Thinklyze на YouTube',
        linkedin: 'Thinklyze в LinkedIn',
      },
    }

    const locale = labels[language as 'tr' | 'en' | 'ru'] ?? labels.en
    return locale[platform]
  }

  return (
    <footer className="bg-black text-white relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-green-900/20 to-transparent"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center">
              <img 
                src="/images/logo_beyaz.webp" 
                alt="Thinklyze" 
                className="h-12"
              />
            </div>
            <p className="text-gray-300 text-sm leading-relaxed font-jakarta">
              {t('footer.description')}
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-300 text-sm font-jakarta">{t('footer.weekdays')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="text-gray-300 text-sm font-jakarta">{t('footer.address')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-gray-300 text-sm font-jakarta">info@thinklyze.com</span>
              </div>
            </div>
            <div className="flex space-x-2">
              <Link to="/contact" className="text-gray-300 hover:text-white text-sm font-jakarta transition-colors duration-200">
                {t('nav.contact')}
              </Link>
              <span className="text-gray-500">•</span>
              <Link to="/about" className="text-gray-300 hover:text-white text-sm font-jakarta transition-colors duration-200">
                {t('nav.about')}
              </Link>
              <span className="text-gray-500">•</span>
              <Link to="/services" className="text-gray-300 hover:text-white text-sm font-jakarta transition-colors duration-200">
                {t('nav.services')}
              </Link>
            </div>
          </div>

          {/* Hizmetlerimiz */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white font-jakarta">{t('footer.services')}</h3>
            <ul className="space-y-2">
              {[
                { name: t('services.googleAds'), path: '/services/google-ads' },
                { name: t('services.metaAds'), path: '/services/meta-ads' },
                { name: t('services.socialMedia'), path: '/services/social-media' },
                { name: t('services.webDesign'), path: '/services/web-design' },
                { name: t('services.seoGeo'), path: '/services/seo-geo' },
                { name: t('services.appDevelopment'), path: '/services/app-development' },
                { name: t('services.marketplace'), path: '/services/marketplace' },
                { name: t('services.shopify'), path: '/services/shopify' },
                { name: t('services.ugcInfluencer'), path: '/services/ugc-influencer' },
                { name: t('services.mentorship'), path: '/services/mentorluk' }
              ].map((item) => (
                <li key={item.name}>
                  <Link to={item.path} className="text-gray-300 hover:text-white transition-colors duration-200 text-sm font-jakarta">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Kaynaklar */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white font-jakarta">{language === 'tr' ? 'Kaynaklar' : language === 'en' ? 'Resources' : 'Ресурсы'}</h3>
            <ul className="space-y-2">
              {[
                { name: language === 'tr' ? 'Blog' : language === 'en' ? 'Blog' : 'Блог', path: '/blog' },
                { name: language === 'tr' ? 'Vaka Çalışmaları' : language === 'en' ? 'Case Studies' : 'Кейсы', path: '/blog' },
                { name: language === 'tr' ? 'Dijital Pazarlama Rehberi' : language === 'en' ? 'Digital Marketing Guide' : 'Руководство по цифровому маркетингу', path: '/blog' },
                { name: language === 'tr' ? 'Webinar' : language === 'en' ? 'Webinar' : 'Вебинар', path: '/academy' },
                { name: language === 'tr' ? 'E-Kitap' : language === 'en' ? 'E-Book' : 'Электронная книга', path: '/blog' },
                { name: language === 'tr' ? 'Araçlar' : language === 'en' ? 'Tools' : 'Инструменты', path: '/marketing-tools' },
                { name: language === 'tr' ? 'Destek Merkezi' : language === 'en' ? 'Support Center' : 'Центр поддержки', path: '/contact' },
                { name: language === 'tr' ? 'SSS' : language === 'en' ? 'FAQ' : 'FAQ', path: '/contact' },
                { name: language === 'tr' ? 'Kariyer' : language === 'en' ? 'Career' : 'Карьера', path: '/career' }
              ].map((item) => (
                <li key={item.name}>
                  <Link to={item.path} className="text-gray-300 hover:text-white transition-colors duration-200 text-sm font-jakarta">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter & Certifications */}
          <div className="space-y-6">
            {/* Newsletter */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white font-jakarta">
                {language === 'tr' ? 'Bülten' : language === 'en' ? 'Newsletter' : 'Рассылка'}
              </h3>
              <p className="text-gray-300 text-sm font-jakarta">
                {language === 'tr' 
                  ? 'Dijital pazarlama dünyasından en güncel haberleri ve ipuçlarını keşfedin.'
                  : language === 'en'
                  ? 'Discover the latest news and tips from the world of digital marketing.'
                  : 'Откройте для себя последние новости и советы из мира цифрового маркетинга.'}
              </p>
              <div className="flex space-x-2">
                <input
                  type="email"
                  placeholder="email@example.com"
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-gray-600 font-jakarta"
                />
                <button className="bg-white text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors duration-200 font-jakarta">
                  {language === 'tr' ? 'Abone Ol' : language === 'en' ? 'Subscribe' : 'Подписаться'}
                </button>
              </div>
              <p className="text-gray-400 text-xs font-jakarta">
                {language === 'tr' 
                  ? 'Kayıt olarak '
                  : language === 'en'
                  ? 'By subscribing, you agree to our '
                  : 'Подписываясь, вы соглашаетесь с нашей '}
                <Link to="/privacy" className="text-white hover:underline">
                  {language === 'tr' ? 'gizlilik politikamızı' : language === 'en' ? 'privacy policy' : 'политикой конфиденциальности'}
                </Link>
                {language === 'tr' && ' kabul etmiş olursunuz.'}
              </p>
            </div>

            {/* Sertifikalar */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white font-jakarta">
                {language === 'tr' ? 'Sertifikalar' : language === 'en' ? 'Certificates' : 'Сертификаты'}
              </h3>
              <div className="flex space-x-4">
                <div className="flex items-center space-x-2 bg-white rounded-lg p-3">
                  <img 
                    src="/images/google-thinklyze.jpg" 
                    alt="Google Partner" 
                    className="w-10 h-10 object-contain"
                  />
                  <div>
                    <p className="text-black text-xs font-medium font-jakarta">Google</p>
                    <p className="text-black text-xs font-jakarta">Partner</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 bg-white rounded-lg p-3">
                  <img 
                    src="/images/metabusiness-thinklyze.webp" 
                    alt="Meta Partner" 
                    className="w-10 h-10 object-contain"
                  />
                  <div>
                    <p className="text-black text-xs font-medium font-jakarta">Meta</p>
                    <p className="text-black text-xs font-jakarta">Partner</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-900 mt-12 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0 mb-4">
              <span className="text-gray-400 text-sm font-jakarta">
                  Thinklyze © {currentYear} {language === 'tr' ? 'Tüm hakları saklıdır' : language === 'en' ? 'All rights reserved' : 'Все права защищены'}.
                </span>
            
            <div className="flex items-center space-x-4">
              <span className="text-gray-400 text-sm font-jakarta">
                {language === 'tr' ? 'Bizi takip edin' : language === 'en' ? 'Follow us' : 'Следуйте за нами'}
              </span>
              <div className="flex space-x-3">
                <a
                  href="#"
                  aria-label={getSocialLabel('twitter')}
                  title={getSocialLabel('twitter')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  aria-label={getSocialLabel('facebook')}
                  title={getSocialLabel('facebook')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/thinklyze/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={getSocialLabel('instagram')}
                  title={getSocialLabel('instagram')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  aria-label={getSocialLabel('youtube')}
                  title={getSocialLabel('youtube')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Youtube className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/thinklyze"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={getSocialLabel('linkedin')}
                  title={getSocialLabel('linkedin')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          
          {/* Legal Links - Centered at Bottom */}
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs font-jakarta pt-4">
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200">
                  {language === 'tr' ? 'Gizlilik Politikası' : language === 'en' ? 'Privacy Policy' : 'Политика конфиденциальности'}
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/delivery-refund" className="text-gray-400 hover:text-white transition-colors duration-200">
                  {language === 'tr' ? 'Teslimat ve İade Şartları' : language === 'en' ? 'Delivery & Refund Terms' : 'Условия доставки и возврата'}
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/distance-sales" className="text-gray-400 hover:text-white transition-colors duration-200">
                  {language === 'tr' ? 'Mesafeli Satış Sözleşmesi' : language === 'en' ? 'Distance Sales Agreement' : 'Договор дистанционных продаж'}
                </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer


